import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeedbackCount, useSubmitFeedback } from '@/shared/api/feedback';

/**
 * "Report an issue" in the help panel footer (ADR-0066, amending dev-rules §4.15): moved here
 * from the old ScreenHelpButton popover unchanged in behaviour (slice-192 step 3): POST
 * /api/feedback with `screenId = pbWindow`, plus the "Open issues" count for the screen.
 */
export function HelpReportIssue({ pbWindow }: { pbWindow: string | null }) {
  const [problemText, setProblemText] = useState('');
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const countQuery = useFeedbackCount(pbWindow ?? '', 'pending', { enabled: pbWindow != null });
  const submit = useSubmitFeedback();
  const openCount = countQuery.data?.count ?? 0;

  // Auto-clear the "Submitted ✓" badge; the count tick is the lasting confirmation.
  useEffect(() => {
    if (submittedAt == null) return;
    const handle = window.setTimeout(() => setSubmittedAt(null), 2200);
    return () => window.clearTimeout(handle);
  }, [submittedAt]);

  const onSubmit = async () => {
    const trimmed = problemText.trim();
    if (!trimmed || !pbWindow || submit.isPending) return;
    try {
      await submit.mutateAsync({ screenId: pbWindow, problemText: trimmed });
      setProblemText('');
      setSubmittedAt(Date.now());
      void countQuery.refetch();
    } catch {
      // submit.error renders inline below.
    }
  };

  return (
    <div className="text-xs" data-testid="help-report-issue">
      <div className="flex items-center justify-between">
        <label
          htmlFor="help-feedback-problem-text"
          className="text-[10px] font-semibold uppercase tracking-wide text-slate-500"
        >
          Report an issue
        </label>
        {pbWindow && (
          <span className="text-[10px] text-slate-500">
            Open issues:{' '}
            <FeedbackCountBadge
              pbWindow={pbWindow}
              count={openCount}
              isLoading={countQuery.isLoading}
              isError={countQuery.isError}
            />
          </span>
        )}
      </div>
      <textarea
        id="help-feedback-problem-text"
        aria-label="problem text"
        value={problemText}
        onChange={(e) => setProblemText(e.target.value)}
        placeholder="What's wrong with this screen?"
        rows={2}
        maxLength={10000}
        className="mt-1 w-full resize-y rounded border border-slate-300 px-2 py-1 text-xs focus:border-phblue-400 focus:outline-none focus:ring-1 focus:ring-phblue-400"
      />
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="min-h-[1rem] flex-1 text-[10px]">
          {!pbWindow && <span className="text-slate-500">Open a screen to report an issue on it.</span>}
          {submit.isError && (
            <span className="text-red-700">
              {submit.error instanceof Error ? submit.error.message : 'Submit failed.'}
            </span>
          )}
          {submittedAt != null && !submit.isError && <span className="text-green-700">Submitted ✓</span>}
        </div>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={!pbWindow || problemText.trim().length === 0 || submit.isPending}
          className="rounded bg-phblue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-phblue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submit.isPending ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

/** "Open issues: N", a Link to the pre-filtered queue when N > 0 (slice-192 step 4). */
function FeedbackCountBadge({
  pbWindow,
  count,
  isLoading,
  isError,
}: {
  pbWindow: string;
  count: number;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) return <span className="text-slate-400">…</span>;
  if (isError) return <span className="text-red-600">unavailable</span>;
  if (count === 0) return <span className="font-medium text-slate-700">0</span>;
  return (
    <Link
      to={`/feedback/queue?screen=${encodeURIComponent(pbWindow)}&status=pending`}
      className="font-medium text-phblue-700 underline hover:text-phblue-800"
    >
      {count}
    </Link>
  );
}
